import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Icon } from "../components/Icon";
import { useAuth } from "../components/AuthContext";
import { appReturnPath } from "../lib/navigation";

export function LandingPage() {
  const { session } = useAuth();
  if (session) return <Navigate to={session.venice.connected ? "/app/catalog" : "/connect-venice"} replace/>;
  return (
    <div className="public-page landing-page">
      <header className="public-header">
        <Logo/>
        <nav><a href="#how">How it works</a><a href="#worlds">World engine</a><Link className="button button--ghost button--small" to="/login">Sign in</Link></nav>
      </header>
      <main>
        <section className="hero">
          <div className="hero-copy">
            <span className="hero-kicker"><Icon name="spark" size={14}/> Private beta</span>
            <h1>Enter worlds that<br/><em>remember you.</em></h1>
            <p>AI-guided roleplaying with tactical maps, persistent characters, and choices that reshape body, mind, identity, and story.</p>
            <div className="hero-actions"><Link className="button button--primary" to="/login">Enter your invitation <Icon name="arrow" size={17}/></Link><a className="button button--text" href="#how">See how it plays</a></div>
            <div className="hero-trust"><span><Icon name="lock" size={14}/>Private campaigns</span><span><Icon name="dice" size={14}/>Server-verified rolls</span><span><Icon name="branch" size={14}/>Branch any turn</span></div>
          </div>
          <div className="hero-visual" aria-label="Tactical story world preview">
            <div className="hero-glow"/>
            <div className="preview-window">
              <div className="preview-top"><span/><span/><span/><b>THE GLASS OBSERVATORY</b><i>Night 2</i></div>
              <div className="preview-body">
                <div className="preview-map">
                  <span className="preview-room room-a">Atrium</span><span className="preview-room room-b">Archive</span><span className="preview-room room-c">Gallery</span>
                  <i className="preview-token token-player">R</i><i className="preview-token token-npc">E</i><i className="preview-token token-object">◆</i>
                  <svg viewBox="0 0 400 300"><path d="M148 192c30-40 52-71 106-71"/></svg>
                </div>
                <div className="preview-story"><span className="preview-label">THE STORY</span><p>The observatory answers your arrival with a low harmonic note. Every pane of glass catches a different version of the room.</p><p className="preview-dialogue">“Choose carefully,” the curator says. “This place remembers decisions better than names.”</p><div className="preview-choice"><Icon name="spark" size={13}/> Inspect the nearest reflection</div></div>
              </div>
            </div>
            <div className="floating-card floating-card--stats"><span>WORLD SHIFT</span><strong>FEM <b>+4</b></strong><strong>COR <b>+2</b></strong></div>
            <div className="floating-card floating-card--save"><Icon name="check"/><div><strong>Turn preserved</strong><span>World version 31</span></div></div>
          </div>
        </section>
        <section className="landing-section" id="how">
          <div className="section-heading"><span>Beyond the chat box</span><h2>Your story becomes a living system.</h2><p>Every answer can move a token, reveal a room, change a relationship, or leave a lasting mark on your character.</p></div>
          <div className="feature-grid">
            <article><span className="feature-icon"><Icon name="map"/></span><h3>A world you can touch</h3><p>Explore persistent tactical maps with rooms, objects, characters, fog, and real distance.</p><b>5 ft tactical grid <Icon name="arrow" size={14}/></b></article>
            <article><span className="feature-icon"><Icon name="body"/></span><h3>Change that matters</h3><p>Transformation is a first-class game system, reflected in your sheet, options, and world.</p><b>Five transformation domains <Icon name="arrow" size={14}/></b></article>
            <article><span className="feature-icon"><Icon name="branch"/></span><h3>Every turn remembered</h3><p>Rewind without erasing your history. Each choice creates a branch you can revisit.</p><b>Persistent versioned saves <Icon name="arrow" size={14}/></b></article>
          </div>
        </section>
        <section className="landing-cta" id="worlds"><span className="hero-kicker"><Icon name="moon" size={14}/> Invitation only</span><h2>Bring a world to life.</h2><p>tfGames.ai is in a small, carefully monitored beta. If you have an invitation, your next story is waiting.</p><Link className="button button--primary" to="/login">Open the door <Icon name="arrow" size={17}/></Link></section>
      </main>
      <footer className="public-footer"><Logo/><span>© 2026 tfGames.ai</span><nav><a href="#">Terms</a><a href="#">Privacy</a><a href="#">Safety</a></nav></footer>
    </div>
  );
}

export function LoginPage() {
  const { session, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const from = appReturnPath(location.state);
  if (session) return session.venice.connected
    ? <Navigate to={from} replace/>
    : <Navigate to="/connect-venice" replace state={{ from }}/>;
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(""); setBusy(true);
    try {
      const nextSession = await login(username, password);
      if (nextSession.venice.connected) navigate(from, { replace: true });
      else navigate("/connect-venice", { replace: true, state: { from } });
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to sign in."); }
    finally { setBusy(false); }
  };
  return <AuthLayout eyebrow="Welcome back" title="Return to your worlds" detail="Your campaigns are private and resume exactly where you left them.">
    <form className="auth-form" onSubmit={(event) => void submit(event)}>
      <label>Username<input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username"/></label>
      <label>Password<span className="label-row"><span/><button type="button">Forgot password?</button></span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password"/></label>
      {error && <div className="form-error"><Icon name="alert" size={16}/>{error}</div>}
      <button className="button button--primary button--wide" disabled={busy}>{busy ? <span className="spinner"/> : <>Enter tfGames.ai <Icon name="arrow" size={17}/></>}</button>
      <p className="auth-note"><Icon name="lock" size={14}/> Access is invitation-only during private beta.</p>
    </form>
  </AuthLayout>;
}

export function InvitePage() {
  const { token = "" } = useParams();
  const { session, acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [checks, setChecks] = useState<Record<string, boolean>>({ adult: false, terms: false, privacy: false, ai: false, sensitive: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (session) return <Navigate to={session.venice.connected ? "/app/catalog" : "/connect-venice"} replace/>;
  const valid = username.length >= 3 && password.length >= 12 && password === confirmation && Object.values(checks).every(Boolean);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true); setError("");
    try { await acceptInvite({ token, username, password, consents: Object.keys(checks) }); navigate("/connect-venice", { replace: true }); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "The invitation could not be accepted."); }
    finally { setBusy(false); }
  };
  const toggle = (key: string) => setChecks((value) => ({ ...value, [key]: !value[key] }));
  return <AuthLayout eyebrow="Invitation accepted" title="Create your explorer" detail="One account, many personas. Your campaign characters remain separate from this login.">
    <form className="auth-form invite-form" onSubmit={(event) => void submit(event)}>
      <label>Username<input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Choose a username" autoComplete="username"/><small>3–32 letters, numbers, hyphens, or underscores. No email required.</small></label>
      <div className="form-pair"><label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password"/></label><label>Confirm<input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password"/></label></div>
      <div className="consent-list">
        {([
          ["adult", "I confirm that I am at least 18 years old."], ["terms", "I accept the Terms of Use."],
          ["privacy", "I have read the Privacy Notice."], ["ai", "I understand that campaign content is AI-generated."],
          ["sensitive", "I consent to processing sensitive private campaign data."],
        ] as Array<[string, string]>).map(([key, label]) => <label className="check-row" key={key}><input type="checkbox" checked={checks[key] ?? false} onChange={() => toggle(key)}/><span><Icon name="check" size={13}/></span>{label}</label>)}
      </div>
      {error && <div className="form-error"><Icon name="alert" size={16}/>{error}</div>}
      <button className="button button--primary button--wide" disabled={!valid || busy}>{busy ? <span className="spinner"/> : <>Create account <Icon name="arrow" size={17}/></>}</button>
    </form>
  </AuthLayout>;
}

function AuthLayout({ eyebrow, title, detail, children }: { eyebrow: string; title: string; detail: string; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <Link className="auth-logo" to="/"><Logo/></Link>
      <div className="auth-atmosphere"><span/><span/><span/></div>
      <main className="auth-card glass-panel">
        <div className="auth-heading"><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{detail}</p></div>
        {children}
      </main>
      <div className="auth-security"><Icon name="shield" size={16}/> Encrypted sessions · Private worlds · You control your saves</div>
    </div>
  );
}
